import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TextField, IconButton, Box, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { MediaI } from '../../../models/home';
import { mediaFilter } from '../../../utils/image';

interface SortableBannerItemProps {
  item: MediaI;
  index: number;
  onRemove: (index: number) => void;
  onUrlChange: (index: number, url: string) => void;
}

const SortableBannerItem: React.FC<SortableBannerItemProps> = ({
  item,
  index,
  onRemove,
  onUrlChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isVideo = item.media && item.media.type.startsWith('video/');
  const isNewVideo = isVideo && item.path_imagen.startsWith('blob:');

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        padding: 2,
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: 'calc((100% - 48px) / 4)',
        minWidth: 200,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box 
          {...listeners}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'grab',
            flex: 1,
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Orden: {item.orden}
          </Typography>
        </Box>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            console.log('eliminar', index)
            onRemove(index)
          }}
          color="error"
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      {isVideo ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" color="primary" fontWeight="bold">
            {isNewVideo ? 'Video (Archivo)' : 'Video (Link de YouTube/Vimeo)'}
          </Typography>
          {!isNewVideo && (
            <TextField
              fullWidth
              size="small"
              placeholder="https://www.youtube.com/embed/..."
              value={item.path_imagen || ''}
              onChange={(e) => onUrlChange(index, e.target.value)}
              label="URL del Video"
            />
          )}
          {item.path_imagen && (
            <Box
              sx={{
                width: '100%',
                height: 150,
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {isNewVideo ? (
                <video
                  src={item.path_imagen}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  controls
                />
              ) : (
                <iframe
                  src={item.path_imagen}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title={`Video ${index}`}
                  allowFullScreen
                />
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" color="secondary" fontWeight="bold">
            Imagen
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 150,
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <img
              src={
                item.path_imagen?.startsWith('blob:')
                  ? item.path_imagen
                  : mediaFilter({ path: item.path_imagen })
              }
              alt={`Banner ${index}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SortableBannerItem;

